import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { Spinner } from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ChevronLeft, CalendarRange, Mail, Plus, Trash2, Play, User } from "lucide-react";
import { TECH_BY_KEY } from "./breathTechniques";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function nextMonday() {
  const d = new Date();
  const add = (8 - d.getDay()) % 7 || 7;
  d.setDate(d.getDate() + add);
  return d.toISOString().slice(0, 10);
}

function PlanCard({ plan, t, navigate, onDelete, pending, onAccept, onDecline }) {
  const slots = plan.slots || [];
  const totalMin = slots.reduce((a, s) => a + (s.duration_min || 0), 0);
  const days = new Set(slots.map((s) => s.day)).size;
  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid={`plan-${plan.id}`}>
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex flex-wrap items-center gap-2">
            {plan.coach_name ? <span className="inline-flex items-center gap-1 rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold text-primary"><User className="h-2.5 w-2.5" />{plan.coach_name}</span> : null}
            {pending ? <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">{t("ui_pending_action")}</span> : null}
          </div>
          <p className="font-display text-base text-foreground">{plan.name}</p>
          {plan.description ? <p className="mt-0.5 text-sm text-muted-foreground">{plan.description}</p> : null}
          <p className="mt-1 text-xs text-muted-foreground">{days} {t("ui_days")} · {slots.length} {t("ui_slots")} · {totalMin} {t("ui_min")}</p>
        </div>
        {!pending && onDelete ? (
          <button onClick={() => onDelete(plan.id)} data-testid={`plan-delete-${plan.id}`} className="shrink-0 rounded-lg p-2 text-muted-foreground hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
        ) : null}
      </div>

      {slots.length > 0 ? (
        <div className="mt-3 space-y-1.5 border-t border-border pt-3">
          {slots.map((s, i) => {
            const tech = TECH_BY_KEY[s.technique];
            const label = tech ? t(`tech_${s.technique}`) : s.technique;
            return (
              <div key={i} className="flex items-center gap-2 text-sm">
                <span className="w-9 shrink-0 text-xs font-medium text-muted-foreground">{DAYS[s.day] || s.day}</span>
                <span className="w-12 shrink-0 text-xs text-muted-foreground">{s.time}</span>
                <span className="min-w-0 flex-1 truncate text-foreground">{label} · {s.duration_min} {t("ui_min")}</span>
                {tech?.techniqueId ? (
                  <button onClick={() => navigate(`/app/breathwork/session/${s.technique}`)} data-testid={`slot-start-${plan.id}-${i}`} className="shrink-0 rounded-full bg-primary/10 p-1.5 text-primary hover:bg-primary/20"><Play className="h-3.5 w-3.5" /></button>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}

      {pending ? (
        <div className="mt-3 flex gap-2">
          <Button variant="outline" onClick={() => onDecline(plan.id)} data-testid={`decline-${plan.id}`} className="flex-1 rounded-full">{t("ui_decline")}</Button>
          <Button onClick={() => onAccept(plan.id)} data-testid={`accept-${plan.id}`} className="flex-[2] gap-1.5 rounded-full">{t("ui_accept_activate")}</Button>
        </div>
      ) : null}
    </div>
  );
}

export default function BreathworkPlan() {
  const t = useT();
  const navigate = useNavigate();
  usePageMeta(t("breathwork_plan_title"));
  const [tab, setTab] = useState("mine");
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [pending, setPending] = useState([]);
  const [templates, setTemplates] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    const [p, pd, tp] = await Promise.all([
      api.get("/breathwork/plans").then((r) => r.data).catch(() => []),
      api.get("/breathwork/plans/pending").then((r) => r.data).catch(() => []),
      api.get("/breathwork/plans/templates").then((r) => r.data).catch(() => []),
    ]);
    setPlans(Array.isArray(p) ? p : []);
    setPending(Array.isArray(pd) ? pd : []);
    setTemplates(Array.isArray(tp) ? tp : []);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const adopt = async (tplId) => {
    try { await api.post("/breathwork/plans/from-template", { template_id: tplId, week_start: nextMonday() }); toast.success(t("popup_adopted")); setTab("mine"); await load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const accept = async (id) => { try { await api.post(`/breathwork/plans/${id}/accept`); await load(); setTab("mine"); } catch (e) { toast.error(apiError(e)); } };
  const decline = async (id) => {
    if (!window.confirm(t("confirm_decline_plan_body"))) return;
    try { await api.post(`/breathwork/plans/${id}/decline`); await load(); } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => {
    if (!window.confirm(t("confirm_delete_plan_body"))) return;
    try { await api.delete(`/breathwork/plans/${id}`); setPlans((ps) => ps.filter((x) => x.id !== id)); } catch (e) { toast.error(apiError(e, t("popup_delete_failed"))); }
  };

  return (
    <div className="space-y-4" data-testid="breathwork-plan">
      <button onClick={() => navigate("/app/breathwork")} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="bp-back">
        <ChevronLeft className="h-4 w-4" /> {t("breathwork")}
      </button>
      <h1 className="font-display text-2xl text-foreground">{t("breathwork_plan_title")}</h1>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="mine" data-testid="bp-tab-mine">{t("ui_my_plans")}</TabsTrigger>
          <TabsTrigger value="templates" data-testid="bp-tab-templates">{t("breathwork_plan_examples")}</TabsTrigger>
          <TabsTrigger value="coach" data-testid="bp-tab-coach">{t("breathwork_plan_from_coach")}{pending.length > 0 ? ` (${pending.length})` : ""}</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? <Spinner label={t("common.loading")} /> : tab === "mine" ? (
        plans.length === 0 ? (
          <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-border py-12 text-center">
            <CalendarRange className="h-8 w-8 text-muted-foreground" />
            <p className="font-display text-foreground">{t("ui_no_plans_title")}</p>
            <p className="max-w-xs text-sm text-muted-foreground">{t("ui_no_plans_sub")}</p>
            <Button onClick={() => setTab("templates")} className="mt-2 gap-1.5 rounded-full"><Plus className="h-4 w-4" /> {t("breathwork_plan_examples")}</Button>
          </div>
        ) : <div className="space-y-3">{plans.map((p) => <PlanCard key={p.id} plan={p} t={t} navigate={navigate} onDelete={del} />)}</div>
      ) : tab === "templates" ? (
        templates.length === 0 ? (
          <div className="py-12 text-center"><p className="font-display text-foreground">{t("ui_templates_empty_title")}</p><p className="mt-1 text-sm text-muted-foreground">{t("ui_templates_empty_sub")}</p></div>
        ) : (
          <div className="space-y-3">
            {templates.map((tpl) => {
              const totalMin = (tpl.slots || []).reduce((a, s) => a + (s.duration_min || 0), 0);
              const days = new Set((tpl.slots || []).map((s) => s.day)).size;
              return (
                <div key={tpl._id} className="rounded-2xl border border-border bg-primary/5 p-4" data-testid={`tpl-${tpl._id}`}>
                  <p className="font-display text-base text-foreground">{tpl.name}</p>
                  <p className="mt-0.5 text-sm text-muted-foreground">{tpl.description}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{days} {t("ui_days")} · {(tpl.slots || []).length} {t("ui_sessions")} · {totalMin} {t("ui_min")}</p>
                  <Button onClick={() => adopt(tpl._id)} data-testid={`adopt-${tpl._id}`} className="mt-3 gap-1.5 rounded-full"><Plus className="h-4 w-4" /> {t("ui_adopt")}</Button>
                </div>
              );
            })}
          </div>
        )
      ) : (
        pending.length === 0 ? (
          <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-border py-12 text-center">
            <Mail className="h-8 w-8 text-muted-foreground" />
            <p className="font-display text-foreground">{t("ui_no_coach_plans_title")}</p>
            <p className="max-w-xs text-sm text-muted-foreground">{t("ui_no_coach_plans_sub")}</p>
          </div>
        ) : <div className="space-y-3">{pending.map((p) => <PlanCard key={p.id} plan={p} t={t} navigate={navigate} pending onAccept={accept} onDecline={decline} />)}</div>
      )}
    </div>
  );
}
